import { Controller, type Control } from "react-hook-form";
import Checkbox from "@/components/Checkbox";
import {
  allowsIncompleteQualifyingLevel,
  isQualifyingLevelCompleteSelection,
  type PgmLevelOption
} from "../createTestPersonPgmLevels";
import type { CreateTestPersonFormValues } from "./types";

type PgmPathSectionProps = {
  control: Control<CreateTestPersonFormValues>;
  options: PgmLevelOption[];
  selectedCodes: string[];
  programType: string;
  disabled?: boolean;
  isProspect?: boolean;
  onToggleLevel: (code: string, current: string[]) => string[];
};

function helperText(programType: string, isProspect: boolean, qlComplete: boolean, selectedCodes: string[]): string {
  if (programType === "ASSOCIATE") {
    if (selectedCodes.includes("FE3")) {
      return "PGM Levels 1–3 are complete so this associate can apply for Membership. Uncheck later levels for mid-path QA, or click F0 twice to leave Qualifying Level in progress.";
    }

    if (qlComplete) {
      return "Qualifying Level is complete: Course Manuals are pre-filled. Check through FE3 to complete PGM Levels 1–3 (required to apply for Membership).";
    }

    return "Qualifying Level is in progress: purchased with Course Manuals open. Check the Qualifying Level to complete it and pre-fill Course Manuals.";
  }

  if (allowsIncompleteQualifyingLevel(programType)) {
    if (qlComplete) {
      return isProspect
        ? "Qualifying Level is complete: Course Manuals are pre-filled and a QE band is provisioned. Uncheck F0 to leave Qualifying Level in progress (purchased, manuals open, PQ-only)."
        : "Qualifying Level is complete: Course Manuals are pre-filled. Uncheck the Qualifying Level to leave it in progress (purchased, Course Manuals open).";
    }

    return isProspect
      ? "Qualifying Level is in progress: F0 is purchased with Course Manuals open (9-month deadline). Check F0 to complete it and pre-fill Course Manuals + QE."
      : "Qualifying Level is in progress: purchased with Course Manuals open. Check the Qualifying Level to complete it and pre-fill Course Manuals.";
  }

  return "Primary-track levels must be contiguous, and at least one completed level is required. For students, F0/A0 are Qualifying Level options for affiliate application eligibility testing.";
}

export default function PgmPathSection({
  control,
  options,
  selectedCodes,
  programType,
  disabled,
  isProspect = false,
  onToggleLevel
}: PgmPathSectionProps) {
  const qlComplete = isQualifyingLevelCompleteSelection(programType, selectedCodes);

  return (
    <div data-testid="create-test-person-pgm-section">
      <div className="text-title-medium mb-1">PGM path</div>
      <p className="text-body-small text-sys-brand-on-surface-variant mb-4" data-testid="pgm-path-helper">
        {helperText(programType, isProspect, qlComplete, selectedCodes)}
      </p>
      <div data-testid="completed-pgm-levels">
        <Controller
          control={control}
          name="completedPgmLevelCodes"
          render={({ field }) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {options.map((option) => (
                <Checkbox
                  key={option.code}
                  name={`completedPgmLevel.${option.code}`}
                  label={option.label}
                  checked={selectedCodes.includes(option.code)}
                  disabled={disabled}
                  onChange={() => {
                    field.onChange(onToggleLevel(option.code, field.value ?? []));
                  }}
                />
              ))}
            </div>
          )}
        />
      </div>
    </div>
  );
}
